import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Alert,
  Link,
  Tooltip,
  Skeleton,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  MarkEmailRead,
  MailOutline,
  Send as SendIcon,
  DoneAll as DoneAllIcon,
  AutoFixHigh as EnrichIcon,
} from '@mui/icons-material';
import { IconFilePlus, IconPrinter } from '@tabler/icons-react';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarCheckbox,
  ToolbarDate,
  ToolbarSpacer,
  ToolbarDivider,
  ToolbarCount,
  ToolbarButton,
} from '../components/toolbar';
import { useNavigate } from 'react-router-dom';
import type {
  InvoicesListItem,
  InvoicesSortField,
} from '@techgitter/contracts';
import { fetchInvoices } from '../services/invoicesApi';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import { Chip } from '../components/chip';
import type { ChipTone } from '../components/chip';
import { NewInvoiceDialog } from '../components/invoices/NewInvoiceDialog';
import { InvoicesViewSwitch } from '../components/invoices/InvoicesViewSwitch';
import {
  enrichInvoice,
  markInvoiceSent,
  sendInvoiceEmail,
} from '../services/xeroIntegrationService';
import type { EnrichmentFields } from '../services/xeroIntegrationService';
import { printInvoiceDocument } from '../services/printApi';
import { printFeedback } from '../utils/printFeedback';

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
});

const formatCurrency = (n: number | null | undefined): string =>
  currencyFormatter.format(Number(n) || 0);

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const statusChipTone = (status: string): ChipTone => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'success';
  if (s === 'outstanding') return 'warning';
  return 'neutral';
};

// Columns that the back end supports sorting on — keep in sync with
// InvoicesSortField and core/services/invoice_service.py:_sort_spec.
type SortableColumn = InvoicesSortField;

const columnLabels: Record<SortableColumn, string> = {
  number: 'Number',
  customerName: 'Customer',
  status: 'Status',
  total: 'Total',
  balanceDue: 'Outstanding',
  dueDate: 'Due',
};

type XeroAction = 'send' | 'mark-sent' | 'enrich';

const ZERO_INVOICE_SENTINEL = 'NA_ZERO_INVOICE';

interface XeroActionGate {
  enabled: boolean;
  reason: string | null;
}

// Reasons mirror the 422 / sentinel cases the Xero integration handlers
// surface today (see api/invoices/__init__.py:_resolve_invoice in
// sm4rt-xero-integration).
const xeroActionGate = (invoice: InvoicesListItem): XeroActionGate => {
  if (!invoice.xeroId) {
    return { enabled: false, reason: 'No Xero invoice linked' };
  }
  if (invoice.xeroId === ZERO_INVOICE_SENTINEL) {
    return { enabled: false, reason: 'Zero-value invoice — no Xero record' };
  }
  if (invoice.xeroResourceType === 'CreditNote') {
    return { enabled: false, reason: 'Credit notes not supported (Phase 2)' };
  }
  return { enabled: true, reason: null };
};

// Maps the Dataverse-shaped enrichment payload returned by /enrich back
// onto the camelCased InvoicesListItem fields, so the row in the grid can
// reflect the new Xero state without a full refetch.
const applyEnrichment = (
  invoice: InvoicesListItem,
  fields: EnrichmentFields
): InvoicesListItem => ({
  ...invoice,
  xeroStatus: fields.cr001_xero_status,
  xeroIsPaid: fields.cr001_xero_is_paid,
  xeroAmountDue: fields.cr001_xero_amount_due,
  xeroAmountPaid: fields.cr001_xero_amount_paid,
  xeroFullyPaidOnDate: fields.cr001_xero_fully_paid_on_date,
  xeroOnlineInvoiceUrl: fields.cr001_xero_online_invoice_url,
});

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface InvoicesPageProps {
  userInfo?: UserInfo | null;
}

export const InvoicesPage: React.FC<InvoicesPageProps> = ({ userInfo }) => {
  const navigate = useNavigate();

  // Pagination state — the table footer is 0-based, the API is 1-based.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Sort state
  const [sort, setSort] = useState<InvoicesSortField>('number');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [showScheduled, setShowScheduled] = useState(true);
  const [showStandard, setShowStandard] = useState(true);
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Data state
  const [items, setItems] = useState<InvoicesListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const isAdmin = userInfo?.roles.includes('admin') ?? false;
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);

  // Per-invoice Xero ops — running flag is keyed by invoice number so the
  // grid can disable just the active row's icons. The send-email action is
  // gated through a confirm dialog because it triggers a real outbound
  // email; mark-sent and enrich go straight through.
  const [xeroRunning, setXeroRunning] = useState<{ number: string; action: XeroAction } | null>(null);
  // Also carries the A4 print's result, whose "skipped" outcome is a warning.
  const [xeroSnackbar, setXeroSnackbar] = useState<
    { open: boolean; message: string; severity: 'success' | 'warning' | 'error' }
  >({ open: false, message: '', severity: 'success' });
  // Invoice number currently printing, so only that row's icon spins.
  const [printingNumber, setPrintingNumber] = useState<string | null>(null);
  const [confirmSend, setConfirmSend] = useState<InvoicesListItem | null>(null);

  // SM4RT's A4 tax invoice (invoice-techsolvers) to the office laser — not
  // RepairShopr's print.
  const printInvoice = useCallback(async (number: string) => {
    setPrintingNumber(number);
    try {
      const result = await printInvoiceDocument(number);
      setXeroSnackbar({ open: true, ...printFeedback(`Invoice ${number}`, result) });
    } catch (err) {
      setXeroSnackbar({
        open: true,
        message: err instanceof Error ? err.message : `Failed to print invoice ${number}`,
        severity: 'error',
      });
    } finally {
      setPrintingNumber(null);
    }
  }, []);

  const runXeroAction = useCallback(
    async (invoice: InvoicesListItem, action: XeroAction) => {
      const number = invoice.number;
      setXeroRunning({ number, action });
      try {
        if (action === 'send') {
          await sendInvoiceEmail(number);
          setXeroSnackbar({
            open: true,
            message: `Sent invoice ${number} via Xero`,
            severity: 'success',
          });
        } else if (action === 'mark-sent') {
          await markInvoiceSent(number);
          setXeroSnackbar({
            open: true,
            message: `Marked invoice ${number} as sent in Xero`,
            severity: 'success',
          });
        } else {
          const result = await enrichInvoice(number);
          setItems((current) =>
            current.map((row) =>
              row.id === invoice.id
                ? applyEnrichment(row, result.enriched_fields)
                : row
            )
          );
          setXeroSnackbar({
            open: true,
            message: `Enriched invoice ${number} from Xero`,
            severity: 'success',
          });
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : `Xero ${action} failed for invoice ${number}`;
        setXeroSnackbar({ open: true, message, severity: 'error' });
      } finally {
        setXeroRunning(null);
      }
    },
    []
  );

  // Debounce the free-text search input so every keystroke doesn't fire a
  // server call. 300ms is the same as CustomersPage / WorkItemSearchPage.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter / sort change resets us to page 0 so the user lands on the
  // start of the new result set.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, unpaidOnly, showScheduled, showStandard, dueFrom, dueTo, sort, order, includeDeleted]);

  // Derive the type param from the two checkboxes. Both checked = no filter.
  // One checked = filter to that type. Neither checked = show nothing (edge
  // case — the UI discourages it but doesn't prevent it).
  const typeParam: 'scheduled' | 'standard' | undefined =
    showScheduled && showStandard
      ? undefined
      : showScheduled
        ? 'scheduled'
        : showStandard
          ? 'standard'
          : 'standard'; // both unchecked — fall back to standard to avoid empty

  // Main fetch effect — fires on every state change that should trigger a
  // reload. AbortController ensures rapid changes don't race.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    fetchInvoices({
      page: page + 1, // API is 1-based
      pageSize: rowsPerPage,
      sort,
      order,
      search: debouncedSearch || undefined,
      unpaidOnly: unpaidOnly || undefined,
      type: typeParam,
      dueFrom: dueFrom || undefined,
      dueTo: dueTo || undefined,
      includeDeleted: includeDeleted || undefined,
      signal: ctrl.signal,
    })
      .then((res) => {
        setItems(res.items);
        setTotalItems(res.pagination.totalItems);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [
    debouncedSearch,
    unpaidOnly,
    typeParam,
    dueFrom,
    dueTo,
    includeDeleted,
    page,
    rowsPerPage,
    sort,
    order,
    retryNonce,
  ]);

  const handleSortClick = useCallback(
    (field: SortableColumn) => {
      if (sort === field) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSort(field);
        setOrder('desc');
      }
    },
    [sort],
  );

  const handleRetry = () => setRetryNonce((n) => n + 1);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const renderEmailed = (emailed: boolean | null) => {
    if (emailed === true) {
      return <MarkEmailRead color="success" fontSize="small" />;
    }
    if (emailed === false) {
      return <MailOutline color="action" fontSize="small" />;
    }
    // null — sm4rt-emailer hasn't shipped yet; show a placeholder.
    return <Box sx={{ color: 'text.disabled' }}>—</Box>;
  };

  const renderXero = (invoice: InvoicesListItem) => {
    // stopPropagation so clicking the chip link doesn't also fire the row's
    // navigate (matches the RS / Xero links in the Links column).
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    // Future-friendly priority ladder. When the Xero integration lands and
    // xeroStatus / xeroOnlineInvoiceUrl are non-null, the chip uses the
    // status string and links to the online invoice.
    if (invoice.xeroStatus && invoice.xeroOnlineInvoiceUrl) {
      return (
        <Chip tone="success" href={invoice.xeroOnlineInvoiceUrl} onClick={stop}>
          {invoice.xeroStatus}
        </Chip>
      );
    }
    if (invoice.xeroIsPaid === true) {
      return invoice.xeroId ? (
        <Chip
          tone="success"
          href={`https://go.xero.com/app/!M-3nX/invoicing/view/${invoice.xeroId}`}
          onClick={stop}
        >
          Paid
        </Chip>
      ) : (
        <Chip tone="success">Paid</Chip>
      );
    }
    if (invoice.xeroId) {
      return (
        <Chip
          href={`https://go.xero.com/app/!M-3nX/invoicing/view/${invoice.xeroId}`}
          onClick={stop}
        >
          Synced
        </Chip>
      );
    }
    return <Box sx={{ color: 'text.disabled' }}>—</Box>;
  };

  // Compact "RS" link rendered in the rightmost column, matching the
  // WorkItemSearchPage pattern. Points at the RepairShopr invoice (not
  // ticket) since this table is the invoice list.
  const renderRsInvoiceLink = (invoice: InvoicesListItem) => {
    if (!invoice.rsInvoiceId) return null;
    return (
      <Link
        href={`https://techsolvers.repairshopr.com/invoices/${invoice.rsInvoiceId}`}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontSize: '0.8rem' }}
        onClick={(e) => e.stopPropagation()}
      >
        RS
      </Link>
    );
  };

  const renderSm4rtJob = (invoice: InvoicesListItem) => {
    if (!invoice.sm4rtJobId) {
      return <Box sx={{ color: 'text.disabled' }}>—</Box>;
    }
    if (!invoice.rsTicketNumber) {
      // The existing /ticket/:number route needs the RS ticket number.
      // Log so data gaps are visible and render a placeholder.
      // eslint-disable-next-line no-console
      console.warn(
        `[InvoicesPage] invoice ${invoice.number} has sm4rtJobId but no rsTicketNumber; cannot link`,
      );
      return <Box sx={{ color: 'text.disabled' }}>—</Box>;
    }
    return (
      <Link
        component="button"
        type="button"
        onClick={() => navigate(`/ticket/${invoice.rsTicketNumber}`)}
        underline="hover"
      >
        Open
      </Link>
    );
  };

  const showingStart = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const showingEnd = Math.min(totalItems, (page + 1) * rowsPerPage);

  return (
    <>
      {/* Contextual toolbar — floating card on the grey page surface
          (main-content--invoices), matching the dashboard. */}
      <SecondaryToolbar floating>
        <InvoicesViewSwitch active="invoices" />
        <ToolbarSearch
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search by number or customer…"
          maxWidth={630}
        />
        <ToolbarCheckbox label="Unpaid only" active={unpaidOnly} onClick={() => setUnpaidOnly((v) => !v)} />
        <ToolbarCheckbox label="Scheduled" active={showScheduled} onClick={() => setShowScheduled((v) => !v)} />
        <ToolbarCheckbox label="Standard" active={showStandard} onClick={() => setShowStandard((v) => !v)} />
        <ToolbarDate label="Due from" value={dueFrom} onChange={setDueFrom} />
        <ToolbarDate label="Due to" value={dueTo} onChange={setDueTo} />
        {isAdmin && (
          <ToolbarCheckbox label="Deleted" active={includeDeleted} onClick={() => setIncludeDeleted((v) => !v)} />
        )}
        <ToolbarSpacer />
        <ToolbarCount>
          {totalItems === 0
            ? 'No results'
            : `Showing ${showingStart}–${showingEnd} of ${totalItems}`}
        </ToolbarCount>
        <ToolbarDivider />
        <ToolbarButton variant="primary" icon={IconFilePlus} onClick={() => setNewInvoiceOpen(true)}>
          New invoice
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
        {/* Error banner */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Table */}
      <DataTableCard>
        <DataTable>
          <thead>
            <tr>
              <DataTableHeadCell sortable active={sort === 'number'} direction={order} onSort={() => handleSortClick('number')}>
                {columnLabels.number}
              </DataTableHeadCell>
              <DataTableHeadCell>Created</DataTableHeadCell>
              <DataTableHeadCell sortable active={sort === 'customerName'} direction={order} onSort={() => handleSortClick('customerName')}>
                {columnLabels.customerName}
              </DataTableHeadCell>
              <DataTableHeadCell>Invoice Name</DataTableHeadCell>
              <DataTableHeadCell sortable active={sort === 'status'} direction={order} onSort={() => handleSortClick('status')}>
                {columnLabels.status}
              </DataTableHeadCell>
              <DataTableHeadCell align="right" sortable active={sort === 'total'} direction={order} onSort={() => handleSortClick('total')}>
                {columnLabels.total}
              </DataTableHeadCell>
              <DataTableHeadCell align="right" sortable active={sort === 'balanceDue'} direction={order} onSort={() => handleSortClick('balanceDue')}>
                {columnLabels.balanceDue}
              </DataTableHeadCell>
              <DataTableHeadCell sortable active={sort === 'dueDate'} direction={order} onSort={() => handleSortClick('dueDate')}>
                {columnLabels.dueDate}
              </DataTableHeadCell>
              <DataTableHeadCell>Job</DataTableHeadCell>
              <DataTableHeadCell>Emailed</DataTableHeadCell>
              <DataTableHeadCell>Xero</DataTableHeadCell>
              <DataTableHeadCell>Links</DataTableHeadCell>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <DataTableRow key={`skeleton-${i}`}>
                  {Array.from({ length: 12 }).map((__, j) => (
                    <DataTableCell key={j}>
                      <Skeleton variant="text" />
                    </DataTableCell>
                  ))}
                </DataTableRow>
              ))}
            {!loading && items.length === 0 && !error && (
              <DataTableEmpty colSpan={12}>No invoices found</DataTableEmpty>
            )}
            {!loading &&
              items.map((invoice) => {
                const dueDateObj = invoice.dueDate
                  ? new Date(invoice.dueDate)
                  : null;
                const isOverdue =
                  dueDateObj !== null &&
                  dueDateObj < today &&
                  (invoice.balanceDue ?? 0) > 0;
                const outstandingPositive = (invoice.balanceDue ?? 0) > 0;
                const truncatedName =
                  invoice.name.length > 40
                    ? `${invoice.name.slice(0, 40)}…`
                    : invoice.name;
                return (
                  <DataTableRow
                    key={invoice.id}
                    onClick={() => navigate(`/invoice/${invoice.number}`)}
                  >
                    <DataTableCell bold>{invoice.number}</DataTableCell>
                    <DataTableCell>{formatDate(invoice.createdAt)}</DataTableCell>
                    <DataTableCell>
                      {invoice.customerDesignation ? (
                        <Tooltip title={invoice.customerDesignation}>
                          <span>{invoice.customerName}</span>
                        </Tooltip>
                      ) : (
                        invoice.customerName || '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {invoice.name.length > 40 ? (
                        <Tooltip title={invoice.name}>
                          <span>{truncatedName}</span>
                        </Tooltip>
                      ) : (
                        truncatedName || '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {invoice.status ? (
                        <Chip tone={statusChipTone(invoice.status)}>{invoice.status}</Chip>
                      ) : (
                        '—'
                      )}
                    </DataTableCell>
                    <DataTableCell align="right">
                      {formatCurrency(invoice.total)}
                    </DataTableCell>
                    <DataTableCell
                      align="right"
                      style={{
                        color: outstandingPositive ? '#d32f2f' : undefined,
                        fontWeight: outstandingPositive ? 600 : undefined,
                      }}
                    >
                      {formatCurrency(invoice.balanceDue)}
                    </DataTableCell>
                    <DataTableCell
                      style={{
                        color: isOverdue ? '#d32f2f' : undefined,
                        fontWeight: isOverdue ? 600 : undefined,
                      }}
                    >
                      {formatDate(invoice.dueDate)}
                    </DataTableCell>
                    <DataTableCell>{renderSm4rtJob(invoice)}</DataTableCell>
                    <DataTableCell>{renderEmailed(invoice.emailed)}</DataTableCell>
                    <DataTableCell>{renderXero(invoice)}</DataTableCell>
                    <DataTableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Print invoice">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Print invoice ${invoice.number}`}
                              disabled={printingNumber !== null}
                              onClick={(e) => {
                                e.stopPropagation();
                                printInvoice(invoice.number);
                              }}
                            >
                              {printingNumber === invoice.number
                                ? <CircularProgress size={14} />
                                : <IconPrinter size={18} stroke={1.5} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {(() => {
                          const gate = xeroActionGate(invoice);
                          const activeAction =
                            xeroRunning && xeroRunning.number === invoice.number
                              ? xeroRunning.action
                              : null;
                          const renderIcon = (
                            action: XeroAction,
                            label: string,
                            Icon: React.ElementType,
                            onClick: () => void,
                          ) => {
                            const tooltip = gate.enabled ? label : (gate.reason ?? label);
                            const isRunning = activeAction === action;
                            const isDisabled =
                              !gate.enabled || (xeroRunning !== null && !isRunning);
                            return (
                              <Tooltip title={tooltip} key={action}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isDisabled}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!gate.enabled) return;
                                      onClick();
                                    }}
                                  >
                                    {isRunning
                                      ? <CircularProgress size={14} />
                                      : <Icon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          };
                          return (
                            <>
                              {renderIcon('send', 'Send invoice via Xero', SendIcon, () =>
                                setConfirmSend(invoice)
                              )}
                              {renderIcon('mark-sent', 'Mark sent in Xero', DoneAllIcon, () =>
                                runXeroAction(invoice, 'mark-sent')
                              )}
                              {renderIcon('enrich', 'Enrich from Xero', EnrichIcon, () =>
                                runXeroAction(invoice, 'enrich')
                              )}
                            </>
                          );
                        })()}
                        {invoice.xeroId && invoice.xeroId !== ZERO_INVOICE_SENTINEL && (
                          <Link
                            href={`https://go.xero.com/app/!M-3nX/invoicing/view/${invoice.xeroId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontSize: '0.8rem', ml: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Xero
                          </Link>
                        )}
                        {renderRsInvoiceLink(invoice)}
                      </Box>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
          </tbody>
        </DataTable>
        <DataTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          count={totalItems}
          rowsPerPageOptions={[25, 50, 100, 200]}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => {
            setRowsPerPage(rpp);
            setPage(0);
          }}
        />
      </DataTableCard>

      <NewInvoiceDialog
        open={newInvoiceOpen}
        onClose={() => setNewInvoiceOpen(false)}
      />

      <Dialog
        open={confirmSend !== null}
        onClose={() => xeroRunning === null && setConfirmSend(null)}
      >
        <DialogTitle>Send invoice via Xero?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will email invoice <strong>{confirmSend?.number}</strong> to{' '}
            <strong>{confirmSend?.customerName || 'the linked Xero contact'}</strong> via Xero.
            This action sends a real email and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmSend(null)}
            disabled={xeroRunning?.action === 'send'}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              xeroRunning?.action === 'send'
                ? <CircularProgress size={14} color="inherit" />
                : <SendIcon />
            }
            disabled={xeroRunning !== null}
            onClick={async () => {
              if (!confirmSend) return;
              const target = confirmSend;
              setConfirmSend(null);
              await runXeroAction(target, 'send');
            }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={xeroSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setXeroSnackbar({ ...xeroSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setXeroSnackbar({ ...xeroSnackbar, open: false })}
          severity={xeroSnackbar.severity}
        >
          {xeroSnackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </>
  );
};
